import {
  useFinalizedPrescriptionExcel,
  useImageArchiveDownload,
} from '@/entities/prescription-registration/hooks/usePrescriptionRegistrations';
import { getApiError, PARTNER_ERROR_CODES } from '@/shared/api/apiError';
import { downloadFromUrl } from '@/shared/lib/download';
import { formatCount } from '@/shared/lib/format';
import { toastApi } from '@/shared/utils/toast';

const NARROW_DOWN = '정산 월·제약사·병의원으로 범위를 좁혀 주세요.';

/** 화면에서 안내하는 오류 문구 — 나머지는 서버 문구, 그것도 없으면 fallback */
const DOWNLOAD_ERROR_MESSAGES: Record<string, string> = {
  [PARTNER_ERROR_CODES.IMAGE_ARCHIVE_TOO_MANY]: `처방 이미지는 한 번에 2,000장까지 받을 수 있어요. ${NARROW_DOWN}`,
  [PARTNER_ERROR_CODES.IMAGE_ARCHIVE_EMPTY]:
    '조건에 맞는 처방 등록 이미지가 없어요.',
  [PARTNER_ERROR_CODES.IMAGE_ARCHIVE_BUSY]:
    '압축 요청이 많아요. 잠시 후 다시 시도해 주세요.',
  [PARTNER_ERROR_CODES.IMAGE_ARCHIVE_JOB_NOT_FOUND]:
    '압축 작업을 찾을 수 없어요. 다시 요청해 주세요.',
  [PARTNER_ERROR_CODES.FINALIZED_EXPORT_TOO_MANY]: `처방 내역은 한 번에 50,000건까지 받을 수 있어요. ${NARROW_DOWN}`,
  [PARTNER_ERROR_CODES.FINALIZED_EXPORT_EMPTY]:
    '조건에 맞는 확정 처방이 없어요.',
};

const toDownloadErrorMessage = (error: unknown, fallback: string) => {
  const apiError = getApiError(error);
  return (
    (apiError && DOWNLOAD_ERROR_MESSAGES[apiError.code]) ||
    apiError?.message?.trim() ||
    fallback
  );
};

const ARCHIVE_ERROR = '처방 등록 이미지를 압축하지 못했어요.';
const EXCEL_ERROR = '확정 처방 내역 엑셀을 만들지 못했어요.';

/**
 * 07 다운로드 — 처방 등록 이미지 ZIP(비동기 압축) · 확정 처방 내역 엑셀(동기).
 * 압축은 모달을 닫아도 이어서 조회해야 해서 화면(페이지)에 둔다. 끝나면 바로 다운로드한다.
 */
export const usePrescriptionDownloads = () => {
  const archive = useImageArchiveDownload({
    onCompleted: (job) => {
      if (job.downloadUrl) downloadFromUrl(job.downloadUrl, job.fileName);
      if (job.skippedCount)
        toastApi.warning(
          `${formatCount(job.skippedCount, '장')}은 원본을 불러오지 못해 빠졌어요.`,
        );
      else toastApi.success('처방 이미지 압축을 마쳤어요.');
    },
    onFailed: (job) =>
      toastApi.error(
        job.status === 'EXPIRED'
          ? '압축 파일이 만료됐어요. 다시 요청해 주세요.'
          : `${ARCHIVE_ERROR} 다시 요청해 주세요.`,
      ),
    onError: (error) =>
      toastApi.error(toDownloadErrorMessage(error, ARCHIVE_ERROR)),
  });

  const excel = useFinalizedPrescriptionExcel({
    onSuccess: (result) => downloadFromUrl(result.downloadUrl, result.fileName),
    onError: (error) =>
      toastApi.error(toDownloadErrorMessage(error, EXCEL_ERROR)),
  });

  return { archive, excel };
};

export type PrescriptionDownloads = ReturnType<typeof usePrescriptionDownloads>;
